import axios from 'axios';
import { toast } from 'react-toastify';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000, // 60 seconds timeout
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    const adminToken = localStorage.getItem('adminToken');

    // Add token based on route type
    if (config.url.includes('/admin/') && adminToken) {
      config.headers.Authorization = `Bearer ${adminToken}`;
    } else if (token && !config.url.includes('/admin/')) {
      config.headers.Authorization = `Bearer ${token}`;
    } else if (adminToken && !config.url.includes('/admin/')) {
      // Allow admins to access user routes using their admin token
      config.headers.Authorization = `Bearer ${adminToken}`;
    }

    // Add request timestamp for debugging
    config.metadata = { startTime: new Date() };

    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors and logging
api.interceptors.response.use(
  (response) => {
    // Log response time in development
    if (process.env.NODE_ENV === 'development') {
      const endTime = new Date();
      const duration = endTime - response.config.metadata.startTime;
      console.log(`API Request: ${response.config.method?.toUpperCase()} ${response.config.url} - ${duration}ms`);
    }

    return response;
  },
  (error) => {
    const { response, request, config } = error;

    // Log error details in development
    if (process.env.NODE_ENV === 'development') {
      const endTime = new Date();
      const duration = endTime - config?.metadata?.startTime;
      console.error(`API Error: ${config?.method?.toUpperCase()} ${config?.url} - ${duration}ms`, error);
    }

    // Handle different error scenarios
    if (response) {
      // Server responded with error status
      const { status, data } = response;

      switch (status) {
        case 401:
          // Unauthorized - clear tokens and redirect
          handleUnauthorized(config?.url);
          break;

        case 403:
          toast.error('Access forbidden. You don\'t have permission to perform this action.');
          break;

        case 404:
          toast.error('Resource not found.');
          break;

        case 422:
          // Validation errors
          if (data?.errors && Array.isArray(data.errors)) {
            data.errors.forEach(err => toast.error(err.msg || err.message));
          } else if (data?.message) {
            toast.error(data.message);
          }
          break;

        case 423:
          // Account locked
          if (data?.message) {
            toast.error(data.message);
          } else {
            toast.error('Account is temporarily locked.');
          }
          break;

        case 429:
          toast.error('Too many requests. Please try again later.');
          break;

        case 500:
          toast.error('Server error. Please try again later.');
          break;

        default:
          // Handle other error messages
          if (data?.message) {
            toast.error(data.message);
          } else {
            toast.error(`Request failed with status ${status}`);
          }
      }
    } else if (request) {
      // Network error or timeout
      if (error.code === 'ECONNABORTED') {
        toast.error('Request timeout. Please check your connection and try again.');
      } else {
        toast.error('Network error. Please check your connection.');
      }
    } else {
      // Other errors
      toast.error('An unexpected error occurred.');
    }

    return Promise.reject(error);
  }
);

// Handle unauthorized access
const handleUnauthorized = (url) => {
  const isAdminRoute = url?.includes('/admin/');

  if (isAdminRoute) {
    // Clear admin tokens
    localStorage.removeItem('adminToken');
    localStorage.removeItem('admin');

    // Redirect to admin login
    if (window.location.pathname !== '/admin/login') {
      window.location.href = '/admin/login';
    }
  } else {
    // Clear user tokens
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    // Redirect to user login
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }
};

// API helper functions
export const apiHelpers = {
  // Handle API response
  handleResponse: (response) => {
    if (response.data.success) {
      return response.data;
    } else {
      throw new Error(response.data.message || 'API request failed');
    }
  },

  // Handle API error
  handleError: (error) => {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('An unexpected error occurred');
    }
  },

  // Get error message from API response
  getErrorMessage: (error) => {
    if (error.response?.data?.message) {
      return error.response.data.message;
    } else if (error.response?.data?.errors) {
      return error.response.data.errors[0]?.msg || 'Validation error';
    } else if (error.message) {
      return error.message;
    } else {
      return 'An unexpected error occurred';
    }
  }
};

// Request helpers for common patterns
export const requestHelpers = {
  // GET request with error handling
  get: async (url, config = {}) => {
    try {
      const response = await api.get(url, config);
      return apiHelpers.handleResponse(response);
    } catch (error) {
      throw apiHelpers.handleError(error);
    }
  },

  // POST request with error handling
  post: async (url, data = {}, config = {}) => {
    try {
      const response = await api.post(url, data, config);
      return apiHelpers.handleResponse(response);
    } catch (error) {
      throw apiHelpers.handleError(error);
    }
  },

  // PUT request with error handling
  put: async (url, data = {}, config = {}) => {
    try {
      const response = await api.put(url, data, config);
      return apiHelpers.handleResponse(response);
    } catch (error) {
      throw apiHelpers.handleError(error);
    }
  },

  // DELETE request with error handling
  delete: async (url, config = {}) => {
    try {
      const response = await api.delete(url, config);
      return apiHelpers.handleResponse(response);
    } catch (error) {
      throw apiHelpers.handleError(error);
    }
  }
};

export default api;